// =============================================
// src/components/BookingCard.tsx
// =============================================
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  IconButton,
  InputAdornment,
  Button,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Grid from '@mui/material/Grid';
import { usePlacesAutocomplete } from '@/hooks/Places';
import { AutocompleteDropdown } from './DropDown';
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useNavigate } from "react-router-dom";
import { setBooking, useAppDispatch, fetchRoutes, BookingPayload } from "@/redux";

const BookingCard: React.FC = () => {
  const [pickup, setPickup] = React.useState('');
  const [dropOff, setDropOff] = React.useState('');
  const dispatch = useAppDispatch();
  const [value, setValue] = React.useState<Dayjs>(dayjs().add(1, "hour").startOf("hour"));
  const [customerRequest, setCustomerRequest] = React.useState('')
  const [open, setOpen] = React.useState(false);
  const pickupPlacesHook = usePlacesAutocomplete(pickup);
  const dropoffPlacesHook = usePlacesAutocomplete(dropOff);
  const navigate = useNavigate();

  const handleNext = (_) => {
    if (!pickupPlacesHook.selectedPlace || !dropoffPlacesHook.selectedPlace) return;

    if (pickupPlacesHook.selectedPlace.placeId === dropoffPlacesHook.selectedPlace.placeId) {
      // set error
      return;
    }
    const bookingPayload: BookingPayload = {

      originPlaceId: pickupPlacesHook.selectedPlace.placeId,
      destinationPlaceId: dropoffPlacesHook.selectedPlace.placeId,
      departureTime: value.toISOString(),
      specialRequests: customerRequest,
      originPlace: pickupPlacesHook.selectedPlace.description,
      destinationPlace: dropoffPlacesHook.selectedPlace.description,
      routeSelectedId: ""
    }

    dispatch(setBooking(bookingPayload))

    dispatch(fetchRoutes(bookingPayload));

    navigate("/routepreview");
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>

      <Card
        sx={{
          bgcolor: 'background.paper',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
          marginTop: '50px',
          maxWidth: {
            xs: '96%',   // phones
            md: '80%',    // tablets
            lg: '70%',    // large desktops
            xl: '70%',    // extra large desktops
          },
          mx: 'auto',     // centers the card
        }}
      >
        <CardContent sx={{ p: { xs: 4, md: 6 } }}>
          <Typography variant="h4" align="center" gutterBottom>
            Route Royale
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            color="text.secondary"
            sx={{ mb: 5 }}
          >
            Luxury Transportation for Every Occasion
          </Typography>

          <Grid container spacing={3}>
            {/* Pickup Location */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <TextField
                  autoComplete="off"

                  label="Pickup Location"
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={pickupPlacesHook.locationSelecting}
                  onFocus={() => {
                    if (pickup.length > 0 && !pickupPlacesHook.locationSelecting) {
                      pickupPlacesHook.reloadSuggestions()
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      pickupPlacesHook.clear();
                    }, 150);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: pickupPlacesHook.locationSelecting ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setPickup("");
                              pickupPlacesHook.setLocationSelecting(false);
                              pickupPlacesHook.clear();
                              pickupPlacesHook.setSetselectedPlace(undefined)
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
                <AutocompleteDropdown
                  suggestions={pickupPlacesHook.suggestions}
                  onSelect={(p) => {
                    pickupPlacesHook.setLocationSelecting(true);
                    setPickup(p.description);
                    pickupPlacesHook.clear();
                    pickupPlacesHook.setSetselectedPlace(p);
                  }}
                />
              </FormControl>
            </Grid>

            {/* Drop-off Location */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <TextField
                  autoComplete="off"

                  label="Drop Off Location"
                  value={dropOff}
                  onChange={(e) => setDropOff(e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={dropoffPlacesHook.locationSelecting}
                  onFocus={() => {
                    if (dropOff.length > 0 && !dropoffPlacesHook.locationSelecting) {
                      dropoffPlacesHook.reloadSuggestions()
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      dropoffPlacesHook.clear();
                    }, 150);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: dropoffPlacesHook.locationSelecting ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setDropOff("");
                              dropoffPlacesHook.setLocationSelecting(false);
                              dropoffPlacesHook.clear();
                              dropoffPlacesHook.setSetselectedPlace(undefined)
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
                <AutocompleteDropdown
                  suggestions={dropoffPlacesHook.suggestions}
                  onSelect={(p) => {
                    dropoffPlacesHook.setLocationSelecting(true);
                    setDropOff(p.description);
                    dropoffPlacesHook.clear();
                    dropoffPlacesHook.setSetselectedPlace(p)

                  }}
                />
              </FormControl>
            </Grid>

            {/* Pick time in Requests */}
            <Grid size={{ xs: 12 }}>
              <DateTimePicker
                sx={{
                  width: '100%'
                }}

                label="Pickup Date and Time"
                openTo="hours" // 👈 opens directly to clock
                minutesStep={30}
                value={value}
                onChange={(newValue) => setValue(newValue)}
                disablePast
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                slotProps={{
                  textField: {
                    onClick: () => setOpen(true), // 👈 THIS is the key
                  },
                }}
              />
            </Grid>
            {/* Special Requests */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                value={customerRequest}
                onChange={(e) => setCustomerRequest(e.target.value)}
                label="Special Requests"
                multiline
                rows={3}
                placeholder="Any special requests..."
              />
            </Grid>
          </Grid>

          {/* View Rates */}
          <Grid
            container
            sx={{ justifyContent: "center" }}
          >
            <Button
              onClick={handleNext}
              sx={{
                fontSize: "1.1rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "primary.main",
              }}
            >
              Next <span style={{ fontSize: "1.3rem" }}>→</span>
            </Button>
          </Grid>
        </CardContent>
      </Card>
    </LocalizationProvider>


  );
};

export default BookingCard;